import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Gauge, Brain, User, Dumbbell, Zap, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useNavigate } from "react-router-dom";
import { ROUTES } from '@/lib/routes';

interface OnboardingDataProps {
  onNext: () => void;
  onBack: () => void;
  dataUploaded: string[];
  setDataUploaded: (data: string[]) => void;
}

export default function OnboardingData({
  onNext,
  onBack,
  dataUploaded,
  setDataUploaded,
}: OnboardingDataProps) {
  const { updateStep } = useOnboarding();
  const navigate = useNavigate();

  const dataTypes = [
    { icon: Activity, name: "GPS & Load", path: ROUTES.LOAD_MONITORING },
    { icon: Dumbbell, name: "Strength", path: ROUTES.FITNESS_TESTING },
    { icon: Zap, name: "Speed", path: ROUTES.SPEED_TESTING },
    { icon: Gauge, name: "Metabolic", path: ROUTES.METABOLIC_TESTING },
    { icon: Brain, name: "Cognitive", path: ROUTES.COGNITIVE_TESTING },
    { icon: User, name: "Body Comp", path: ROUTES.BODY_COMPOSITION },
  ];

  const handleNext = async () => {
    await updateStep("step_data", true);
    onNext();
  };

  const handleDataTypeClick = async (path: string, name: string) => {
    await updateStep("step_data", true);
    navigate(path);
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold">Upload Performance Data</h2>
        <p className="text-muted-foreground">
          Add your first performance data to unlock insights. You can skip and upload later.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {dataTypes.map((type) => {
                const Icon = type.icon;
                const isUploaded = dataUploaded.includes(type.name);

                return (
                  <Card
                    key={type.name}
                    className={`hover:border-primary/50 transition-colors cursor-pointer ${
                      isUploaded ? "bg-primary/5 border-primary/20" : ""
                    }`}
                    onClick={() => handleDataTypeClick(type.path, type.name)}
                  >
                    <CardContent className="flex flex-col items-center justify-center p-6 space-y-3">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-center">{type.name}</h3>
                      {isUploaded && (
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="bg-muted">
              <CardContent className="py-4">
                <p className="text-sm text-center text-muted-foreground">
                  <strong>Optional:</strong> Click any data type to upload. You can always add more data later from the Import Hub.
                </p>
              </CardContent>
            </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-8">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleNext}>
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
